import React, { useEffect, useMemo, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useLocation, Navigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import './App.css';
import { HiMenu, HiX } from "react-icons/hi";
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from "./components/ScrollToTop";

import Main from './pages/Main';
import Filmography from './pages/Filmography';
import Notice from './pages/Notice';
import Info from './pages/Info';
import Gallery from './pages/Gallery';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import NoticeDetail from './pages/NoticeDetail';

import AdminLogin from './pages/AdminLogin';
import AdminPage from './pages/AdminPage';
import AdminAnalysis from './pages/AdminAnalysis';
import MainPopup from './components/MainPopup';
import MailModal from './components/MailModal';

import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const GA_TRACKING_ID = "G-QCVH7R2Y2W";

/**
 * 1. Coming Soon 컴포넌트
 */
function ComingSoon() {
  return (
    <div style={{
      backgroundColor: '#0f0f0f', 
      color: '#d8c8a8', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center',
      fontFamily: "'Pretendard', sans-serif",
      textAlign: 'center',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 9999
    }}>
      <div style={{ opacity: 0, transform: 'translateY(20px)', animation: 'fadeIn 1.2s forwards' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '300', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '10px' }}>
          HANJIWOO
        </h1>
        <div style={{ width: '40px', height: '1px', background: '#c4a77d', margin: '30px auto' }}></div>
        <p style={{ fontSize: '1rem', opacity: 0.7, letterSpacing: '0.1em' }}>
          한지우 배우 홈페이지 오픈 준비 중입니다.
        </p>
        <p style={{ fontSize: '0.8rem', marginTop: '20px', opacity: 0.5 }}>Coming Soon</p>
      </div>
      <style>{`
        @keyframes fadeIn { to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

function AdminGuard({ user, children }) {
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin-login" replace />;
  }
  return children;
}

function AppInner({ user, setUser }) {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isMailOpen, setIsMailOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'classic');
  const [footerBg, setFooterBg] = useState(false);
  const [activeUsers, setActiveUsers] = useState(0);

  // --- 🎵 BGM 로직 시작 ---
  const audioRef = useRef(null);
  const fadeInterval = useRef(null);
  const [isPlaying, setIsPlaying] = useState(() => {
    const saved = localStorage.getItem('bgm_enabled');
    return saved === null ? true : saved === 'true';
  });

  // 볼륨 페이드 인 함수
  const fadeIn = () => {
    if (!audioRef.current) return;
    clearInterval(fadeInterval.current);
    
    audioRef.current.play().then(() => {
      fadeInterval.current = setInterval(() => {
        if (audioRef.current && audioRef.current.volume < 0.45) {
          audioRef.current.volume = Math.min(audioRef.current.volume + 0.05, 0.5);
        } else {
          clearInterval(fadeInterval.current);
        }
      }, 150);
    }).catch(e => {
      console.log("재생 대기 중 (상호작용 필요):", e.name);
    });
  };

  useEffect(() => {
    ReactGA.initialize(GA_TRACKING_ID);
  }, []);

  useEffect(() => {
    // 현재 경로(location.pathname)를 구글 서버로 보냅니다.
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    const excludedPaths = ["/admin", "/admin-login", "/admin/analysis"];
    const isExcluded = excludedPaths.some(path => location.pathname.startsWith(path));

    const reportActivity = async () => {
      if (isExcluded) return;
      try {
        const res = await fetch(`${API_BASE}/api/monitor.php`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          setActiveUsers(data.active_users); // 3. 받아온 숫자를 상태에 저장
          // console.log("현재 접속자:", data.active_users);
        }
      } catch (err) {
        // console.error("Monitor 연동 실패:", err);
      }
    };

    reportActivity();

    let timer;
    if (!isExcluded) {
      timer = setInterval(reportActivity, 4.5 * 60 * 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    audioRef.current = new Audio("/assets/bgm.mp3");
    audioRef.current.loop = true;
    audioRef.current.volume = 0; 

    const initAudio = () => {
      if (localStorage.getItem('bgm_enabled') !== 'false') {
        fadeIn();
      }
      window.removeEventListener('click', initAudio);
      window.removeEventListener('touchstart', initAudio);
    };

    window.addEventListener('click', initAudio);
    window.addEventListener('touchstart', initAudio);

    const handleVideoPlay = () => {
      clearInterval(fadeInterval.current);
      audioRef.current?.pause();
    };

    const handleVideoPause = () => {
      // [수정 포인트] 최신 isPlaying 상태와 로컬스토리지를 모두 확인
      const savedBgmStatus = localStorage.getItem('bgm_enabled');
      const shouldPlay = savedBgmStatus === null ? true : savedBgmStatus === 'true';
      
      if (shouldPlay && isPlaying) {
        fadeIn();
      }
    };

    // const handleVideoPlay = () => audioRef.current?.pause();
    // const handleVideoPause = () => { if (isPlaying) fadeIn(); };
    window.addEventListener('videoPlaying', handleVideoPlay);
    window.addEventListener('videoPaused', handleVideoPause);

    return () => {
      window.removeEventListener('click', initAudio);
      window.removeEventListener('touchstart', initAudio);
      window.removeEventListener('videoPlaying', handleVideoPlay);
      window.removeEventListener('videoPaused', handleVideoPause);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      clearInterval(fadeInterval.current);
    };
  }, [isPlaying]);

  const toggleBgm = (e) => {
    e.stopPropagation();
    const nextState = !isPlaying;
    setIsPlaying(nextState);
    localStorage.setItem('bgm_enabled', String(nextState));

    if (nextState) {
      fadeIn();
    } else {
      clearInterval(fadeInterval.current);
      audioRef.current?.pause();
    }
  };

  useEffect(() => {
    if (isPlaying && audioRef.current && audioRef.current.paused) {
      fadeIn();
    }
  }, [location.pathname, isPlaying]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    window.dispatchEvent(new Event('themechange'));
  }, [theme]);

  // const isComingSoon = useMemo(() => location.pathname === '/', [location.pathname]);
  const isHome = useMemo(() => location.pathname === '/', [location.pathname]);

  useEffect(() => {
    if (isHome) {
      document.body.classList.add('is-home-active');
    } else {
      document.body.classList.remove('is-home-active');
    }
    
    // 페이지 이동 시 메뉴 닫기 및 스크롤 잠금 해제 안전장치
    return () => document.body.classList.remove('is-home-active');
  }, [isHome]);

  useEffect(() => {
    const handleScroll = () => {
      // 메인 페이지일 때의 로직
      if (isHome) {
        // 스크롤이 일정량(예: 100px 또는 section1 높이만큼) 내려갔을 때 배경 활성화
        if (window.scrollY > 100) {
          setFooterBg(true);
        } else {
          setFooterBg(false);
        }
      } else {
        // 메인 페이지가 아닐 때는 항상 배경 활성화
        setFooterBg(true);
      }
    };

    // 초기 실행
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  async function logout() {
    try {
      await fetch(`${API_BASE}/api/logout.php`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    setUser(null);
  }

  const handleLangChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };

  return (
    <div className={`App ${isHome ? 'is-home' : ''}`} data-theme={theme}>
      
      {/* {!isComingSoon && isHome && <MainPopup />} */}
      {isHome && <MainPopup />}
      {/* {!isComingSoon && ( */}
        <div 
          className={`bgm-controller ${isPlaying ? 'is-playing' : ''}`} 
          onClick={toggleBgm}
        >
          <div className="wave-container">
            <div className="wave-bar"></div>
            <div className="wave-bar"></div>
            <div className="wave-bar"></div>
          </div>
          <span className="bgm-text">{isPlaying ? 'SOUND ON' : 'SOUND OFF'}</span>
        </div>
      {/* )} */}

      {/* {!isComingSoon && ( */}
        <nav className="navbar">
          {/* <Link to="/home" className="logo">HAN JIWOO</Link> */}
          <Link to="/" className="logo">HAN JIWOO</Link>
          <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
          </button>

          <div className={`nav-links ${isMenuOpen ? 'is-open' : ''}`}>
            {/* <NavLink to="/home" end className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_main')}</NavLink> */}
            <NavLink to="/" end className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_main')}</NavLink>
            <NavLink to="/filmography" className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_filmo')}</NavLink>
            <NavLink to="/gallery" className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_gallery')}</NavLink>
            <NavLink to="/news" className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_news')}</NavLink>
            <NavLink to="/notice" className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_notice')}</NavLink>
            <NavLink to="/info" className={({ isActive}) => `nav-link ${isActive ? 'is-active' : ''}`}>{t('nav_info')}</NavLink>
            {user?.role === 'admin' && <Link to="/admin">ADMIN</Link>}

            <div className="mobile-only-actions">
               <select className="lang-select" value={i18n.language} onChange={handleLangChange}>
                  <option value="KR">KR</option>
                  <option value="EN">EN</option>
                  <option value="JP">JP</option>
                  <option value="CH">CH</option>
               </select>
               <button className="theme-toggle" onClick={() => setTheme(prev => (prev === 'classic' ? 'cinema' : 'classic'))}>
                  {theme === 'classic' ? 'CINEMA' : 'CLASSIC'}
               </button>
            </div>
          </div>

          <div className="nav-actions desktop-only">
            <select className="lang-select" value={i18n.language} onChange={handleLangChange}>
              <option value="KR">KR</option>
              <option value="EN">EN</option>
              <option value="JP">JP</option>
              <option value="CH">CH</option>
            </select>
            <button type="button" className="theme-toggle" onClick={() => setTheme(prev => (prev === 'classic' ? 'cinema' : 'classic'))}>
              {theme === 'classic' ? 'CINEMA' : 'CLASSIC'}
            </button>
            {user?.role === 'admin' && (
              <button type="button" className="theme-toggle" onClick={logout} style={{ marginLeft: 8 }}>LOGOUT</button>
            )}
          </div>
        </nav>

      {/* <div className={isComingSoon ? "" : "page-content"}> */}
        <div className="page-content">
        <Routes>
          {/* <Route path="/" element={<ComingSoon />} /> */}
          {/* <Route path="/home" element={<Main />} /> */}
          <Route path="/" element={<Main />} />
          <Route path="/filmography" element={<Filmography />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/news" element={<News />} />
          <Route path="/notice" element={<Notice />} />
          <Route path="/info" element={<Info />} />
          <Route path="/admin-login" element={<AdminLogin setUser={setUser} />} />
          <Route path="/admin" element={<AdminGuard user={user}><AdminPage user={user} /></AdminGuard>} />
          <Route path="/admin/analysis" element={<AdminGuard user={user}><AdminAnalysis user={user} /></AdminGuard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/news/:id" element={<NewsDetail />} />
          <Route path="/notice/:id" element={<NoticeDetail />} />
        </Routes>
      </div>

      <footer className={`footer ${footerBg ? 'is-bg-active' : ''}`}>
        <div className="footer-inner">
          <p>© 2026 Actor Han Jiwoo. All rights reserved.</p>
          <button className="footer-support-link" onClick={() => setIsMailOpen(true)}>
            <span className="icon">✉</span> Contact
          </button>
        </div>
      </footer>
      <MailModal isOpen={isMailOpen} onClose={() => setIsMailOpen(false)} />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [meLoaded, setMeLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/me.php`, { credentials: 'include' });
        const data = await res.json();
        setUser(data?.user || null);
      } catch {
        setUser(null);
      } finally {
        setMeLoaded(true);
      }
    })();
  }, []);

  if (!meLoaded) return null;
  

  const handleContextMenu = (e) => {
    if (window.location.pathname.startsWith('/admin')) return;
    e.preventDefault();
  };

  const handleDragStart = (e) => {
    if (window.location.pathname.startsWith('/admin')) return;
    e.preventDefault();
  };

  return (
    <HelmetProvider>
      <div className="app-protection-wrapper" onContextMenu={handleContextMenu} onDragStart={handleDragStart} >
        <Router>
          <ScrollToTop />
          <AppInner user={user} setUser={setUser} />
        </Router>
      </div>
    </HelmetProvider>
  );
}